import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Calendar,
  DollarSign,
  FileText,
  CheckCircle,
  Building,
  Hammer,
  Camera,
  XCircle,
  Receipt,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import { useTranslation } from "react-i18next";

const getExpenseTypes = (t) => [
  { id: 1, name: t("materials"), color: "#EF4444", bg: "#FEF2F2" },
  { id: 2, name: t("equipment"), color: "#8B5CF6", bg: "#F5F3FF" },
  { id: 3, name: t("transportation"), color: "#3B82F6", bg: "#EFF6FF" },
  { id: 4, name: t("utilities"), color: "#F59E0B", bg: "#FFFBEB" },
  { id: 5, name: t("otherExpense"), color: "#6B7280", bg: "#F9FAFB" },
];

export default function BossCreateExpenses({
  visible,
  onClose,
  onSuccess,
  preselectedDate = "",
  preselectedPlaceId = null,
}) {
  const { t } = useTranslation();
  const expenseTypes = getExpenseTypes(t);
  const [allData, setAllData] = useState(null);
  const [userData, setUserData] = useState(null);

  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [price, setPrice] = useState("");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const fileInputRef = useRef(null);

  // Load from localStorage
  useEffect(() => {
    const data = localStorage.getItem("allData");
    const user = localStorage.getItem("user");
    if (data) setAllData(JSON.parse(data));
    if (user) setUserData(JSON.parse(user));
  }, []);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  // Initialise fields when modal opens
  useEffect(() => {
    if (visible) {
      setDate(preselectedDate || getTodayDate());
      setSelectedPlaceId(preselectedPlaceId);
    }
  }, [visible, preselectedDate, preselectedPlaceId]);

  const handleClose = () => {
    setSelectedPlaceId(null);
    setSelectedType(null);
    setPrice("");
    setDate("");
    setNote("");
    setImageFile(null);
    setImagePreview(null);
    onClose();
  };

  // ── Image handling ────────────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    // Reset so same file can be picked again
    e.target.value = "";
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleCreateExpense = async () => {
    if (!date || date.trim() === "") {
      alert("Please enter a date");
      return;
    }
    if (
      !price ||
      price.trim() === "" ||
      isNaN(price) ||
      parseFloat(price) <= 0
    ) {
      alert("Please enter a valid price");
      return;
    }
    if (!selectedType) {
      alert("Please select an expense type");
      return;
    }
    if (!userData) {
      alert("User data not found");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append(
        "UserId",
        userData.type === 0 ? userData.id : userData.workWith,
      );
      formData.append("PlaceId", selectedPlaceId || "");
      formData.append("Title", "");
      formData.append("Price", parseInt(price));
      formData.append("Note", note.trim());
      formData.append("Date", date);
      formData.append("Type", selectedType);
      formData.append("WhoMake", userData.id);
      formData.append("WithInvoice", 2);

      if (imageFile) {
        formData.append("file", imageFile);
      }

      const response = await axios.post(
        `${MyDomain}Expenses/CreateExpensesWithImage`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (response.status === 200) {
        const newExpense = response.data;

        // Update localStorage
        const currentData = JSON.parse(localStorage.getItem("allData") || "{}");
        const updated = {
          ...currentData,
          myExpenses: [...(currentData.myExpenses || []), newExpense],
        };
        localStorage.setItem("allData", JSON.stringify(updated));

        handleClose();
        if (onSuccess) onSuccess(newExpense);
        alert("Expense created successfully!");
      }
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
      alert("Failed to create expense");
    } finally {
      setIsLoading(false);
    }
  };

  if (!visible || !allData) return null;

  const selectedPlace = (allData.myPlaces || []).find(
    (p) => p.id === selectedPlaceId,
  );

  return (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center z-50"
      style={{
        background: "rgba(241,245,249,0.75)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[92vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="border-b border-gray-100 px-5 pt-3 pb-4 rounded-t-3xl">
          <div className="w-9 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Receipt className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{t("addPayment")}</h2>
                <p className="text-sm text-gray-500">{t("expenseDetails")}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* ── Scrollable form ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="space-y-5">
            {/* Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("date")} <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <div className="flex-1 flex items-center bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus-within:border-yellow-400 transition">
                  <Calendar className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                  <input
                    type="text"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    placeholder="DD-MM-YYYY"
                    maxLength={10}
                    className="flex-1 bg-transparent outline-none text-gray-900"
                  />
                </div>
                <button
                  onClick={() => setDate(getTodayDate())}
                  className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-white px-4 py-3 rounded-xl transition font-semibold text-sm"
                >
                  <Calendar className="w-4 h-4" />
                  Today
                </button>
              </div>
            </div>

            {/* Expense Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("otherExpense")} <span className="text-red-500">*</span>
              </label>
              <div className="overflow-x-auto pb-1 hide-scrollbar">
                <div className="flex gap-3" style={{ width: "max-content" }}>
                  {expenseTypes.map((type) => {
                    const isActive = selectedType === type.id;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setSelectedType(type.id)}
                        className="relative w-28 flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition"
                        style={{
                          borderColor: isActive ? type.color : "#E5E7EB",
                          backgroundColor: isActive ? type.bg : "#F9FAFB",
                        }}
                      >
                        {isActive && (
                          <CheckCircle
                            className="absolute top-2 right-2 w-4 h-4"
                            style={{ color: type.color }}
                          />
                        )}
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${type.color}20` }}
                        >
                          <Receipt
                            className="w-6 h-6"
                            style={{ color: type.color }}
                          />
                        </div>
                        <span
                          className="text-xs font-semibold text-center"
                          style={{ color: isActive ? type.color : "#374151" }}
                        >
                          {type.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("amount")} <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus-within:border-yellow-400 transition">
                <DollarSign className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="Enter amount"
                  className="flex-1 bg-transparent outline-none text-gray-900"
                />
                <span className="text-gray-500 font-semibold ml-2">₪</span>
              </div>
            </div>

            {/* Image picker */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Image (Optional)
              </label>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />

              {imagePreview ? (
                <div className="relative rounded-xl overflow-hidden border-2 border-gray-200">
                  <img
                    src={imagePreview}
                    alt="Expense"
                    className="w-full h-52 object-cover"
                  />
                  <button
                    onClick={removeImage}
                    className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-red-50 transition"
                  >
                    <XCircle className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-3 py-12 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition"
                >
                  <Camera className="w-8 h-8 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">
                    Add Photo
                  </span>
                </button>
              )}
            </div>

            {/* Place Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("place")} (Optional)
              </label>

              {/* Selected place display */}
              {selectedPlace && (
                <div className="flex items-center justify-between bg-yellow-50 border-2 border-yellow-400 rounded-xl p-3 mb-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center flex-shrink-0">
                      {selectedPlace.placeType === 1 ? (
                        <Building className="w-5 h-5 text-yellow-600" />
                      ) : (
                        <Hammer className="w-5 h-5 text-yellow-600" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                        {t("place")}
                      </p>
                      <p className="text-sm font-bold text-yellow-800 truncate">
                        {selectedPlace.place}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedPlaceId(null)}
                    className="p-1 ml-2 flex-shrink-0"
                  >
                    <XCircle className="w-5 h-5 text-red-400" />
                  </button>
                </div>
              )}

              {(allData.myPlaces || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                  <Building className="w-10 h-10 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">{t("noData")}</p>
                </div>
              ) : (
                <div className="overflow-x-auto pb-1 hide-scrollbar">
                  <div className="flex gap-3" style={{ width: "max-content" }}>
                    {(allData.myPlaces || []).map((place) => {
                      const isActive = selectedPlaceId === place.id;
                      return (
                        <button
                          key={place.id}
                          onClick={() => setSelectedPlaceId(place.id)}
                          className={`relative w-32 flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition ${
                            isActive
                              ? "bg-yellow-50 border-yellow-400"
                              : "bg-gray-50 border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          {isActive && (
                            <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-yellow-500" />
                          )}
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              isActive ? "bg-yellow-200" : "bg-gray-200"
                            }`}
                          >
                            {place.placeType === 1 ? (
                              <Building
                                className={`w-6 h-6 ${isActive ? "text-yellow-500" : "text-gray-500"}`}
                              />
                            ) : (
                              <Hammer
                                className={`w-6 h-6 ${isActive ? "text-yellow-500" : "text-gray-500"}`}
                              />
                            )}
                          </div>
                          <p
                            className={`text-xs font-semibold text-center leading-tight min-h-[2rem] ${
                              isActive ? "text-yellow-600" : "text-gray-700"
                            }`}
                          >
                            {place.place}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("note")} (Optional)
              </label>
              <div className="flex items-start gap-3 bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus-within:border-yellow-400 transition">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("note")}
                  rows={3}
                  maxLength={200}
                  className="flex-1 bg-transparent outline-none text-gray-900 resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-gray-100 px-5 py-4">
          <button
            onClick={handleCreateExpense}
            disabled={isLoading}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold transition ${
              isLoading
                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                : "bg-yellow-400 hover:bg-yellow-500 text-white shadow-md"
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t("loading")}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>{t("addPayment")}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin   { animation: spin 1s linear infinite; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}
